export const valueChains = {
  // Pharmaceutical Sector
  pharma: {
    type: 'industry',
    stages: [
      {
        id: 'imports',
        name: 'Imports',
        content: 'Import of pharmaceutical products, raw materials, and APIs from international markets'
      },
      {
        id: 'markets',
        name: 'Markets',
        content: 'Biopharma, Patented Biologics, Biosimilars, Conventional Pharma, Patented Chemicals, Generics'
      },
      {
        id: 'research',
        name: 'Research & Development',
        content: 'Biopharma R&D, Conventional R&D, Research Services, Testing & Bioequivalence Study'
      },
      {
        id: 'clinical-trials',
        name: 'Clinical Trials',
        content: 'Clinical Trials for new drug formulations, biosimilars, and generics'
      },
      {
        id: 'production-inputs',
        name: 'Production Inputs',
        content: 'Excipients, Fine Chemicals, Active Pharmaceutical Ingredients'
      },
      {
        id: 'primary-manufacturing',
        name: 'Primary Manufacturing',
        content: 'Biologic API, Chemical API production and synthesis'
      },
      {
        id: 'final-dosage',
        name: 'Final Dosage Formulation',
        content: 'Solid Dosage Formulation, Injectable Dosage Formulation, Liquid/Semi-Solid Dosage Formulation'
      },
      {
        id: 'packaging',
        name: 'Packaging',
        content: 'Biologics Fill & Finish, Chemical Fill & Finish, Primary Packaging'
      },
      {
        id: 'distribution',
        name: 'Marketing & Distribution',
        content: 'Marketing (go-to-market), Warehousing & Logistics cold chains, Hospitals, Retail market'
      },
      {
        id: 'exports',
        name: 'Exports',
        content: 'GCC, MENA, OIC, Global markets for pharmaceutical exports'
      }
    ]
  },

  // Biotechnology Sub-Industry
  biopharma: {
    type: 'subIndustry',
    parentId: 'pharma',
    stages: [
      {
        id: 'import',
        name: 'Import',
        content: 'Import',
        topImportCategories: {
          headers: ["Category", "Share of Imports", "Top Supplier Countries"],
          rows: [
            ["Monoclonal Antibodies", "35%", "USA, Switzerland, Germany"],
            ["Vaccines", "25%", "Belgium, USA, India"],
            ["Insulin & Analogs", "20%", "Denmark, France, USA"],
            ["Cell/Gene Therapies", "15%", "USA, UK, South Korea"],
            ["Growth Factors", "5%", "Japan, Germany"]
          ]
        },
        dominantSuppliers: [
          "USA (40% share): Pfizer, Merck, Gilead.",
          "EU (35%): Roche, Novo Nordisk, Sanofi.",
          "Asia (20%): Serum Institute (India), Samsung Biologics (Korea)."
        ],
        criticalDependencies: [
          "mRNA Vaccines: 100% imported (despite SPARK's future capacity).",
          "CAR-T Therapies: Entirely sourced from USA/EU ($500K+/dose)."
        ],
        importReductionPlan: {
          text: "Target: Cut biologics imports to 50% by 2030 via:",
          points: [
            "Local production (e.g., SPARK's mRNA plant).",
            "Halal biotech partnerships with Malaysia/Indonesia."
          ]
        }
      },
      {
        id: 'researchandiscovery',
        name: 'Research & Discovery',
        content: 'Basic Research, Translational Research, AI/Computational Bio',
        summary: "Saudi Arabia has strong genomic data (60% population carries diabetes/genetic disorder markers) but lags in translational research. Only 3% of VC funding targets early-stage biotech (Magnitt 2023).",
        productsServicesNeeded: [
          "AI-driven drug discovery platforms (e.g., for Arab-specific diseases)",
          "Shared GLP lab infrastructure"
        ],
        keyPlayers: [
          "KAIMRC: Leading Arab genome sequencing ([Project](https://www.kaimrc.med.sa/))",
          "KAUST AI Initiative: Accelerating drug design ([Link](https://cemse.kaust.edu.sa/ai))"
        ],
        trends: "Halal-compliant R&D, AI leapfrogging.",
        challenges: "Weak academia-industry links, low private investment.",
        riskRating: "High (4/5) – Dependent on foreign IP.",
        policyRecommendations: [
          "Tax credits for private-sector R&D partnerships.",
          "Mandate data sharing from hospitals to KAIMRC/KAUST."
        ]
      },
      {
        id: 'development',
        name: 'Development',
        content: 'Preclinical Trials, Clinical Trials, Regulatory Approval',
        summary: "SFDA's WHO ML4 status (2023) speeds approvals, but only 5% of trials target Arab-specific diseases (SFDA report).",
        productsServicesNeeded: [
          "Local CROs for patient recruitment",
          "GMP-certified trial sites"
        ],
        keyPlayers: [
          "SFDA: Fast-track pathways ([Regulations](https://www.sfda.gov.sa/en))",
          "Cigalah-Medgulf: Expanding clinical trial capacity ([News](https://www.argaam.com))"
        ],
        trends: "Decentralized trials (DCTs), MERS/diabetes focus.",
        challenges: "Slow patient recruitment, lack of specialized sites.",
        riskRating: "Medium (3/5) – Regulatory progress offsets talent gaps.",
        policyRecommendations: [
          "Subsidies for trials targeting local diseases (e.g., sickle cell).",
          "Streamline ethics approvals via digital portals."
        ]
      },
      {
        id: 'manufacturing',
        name: 'Manufacturing',
        content: 'Biologics Production, Biosimilars, Fill-Finish',
        summary: "90% of biomanufacturing materials are imported, but mRNA/biosimilar facilities are rising (e.g., SPARK's $1.2B plant).",
        productsServicesNeeded: [
          "Local fill-finish capacity",
          "Halal-certified cell culture media"
        ],
        keyPlayers: [
          "BioNTech-SPARK: mRNA vaccine production ([Link](https://www.spark.kaec.net))"
        ],
        trends: "Continuous bioprocessing, MENA vaccine exports.",
        challenges: "High Capex, supply chain fragility.",
        riskRating: "Critical (5/5) – Geopolitical disruptions could halt production.",
        policyRecommendations: [
          "Local content mandates (e.g., 30% raw materials by 2030).",
          "Co-investment funds for modular factories."
        ]
      },
      {
        id: 'commercialization',
        name: 'Commercialization',
        content: 'Market Access & Buyers, Pricing & Reimbursement',
        summary: "Hospitals prefer imports (70% market share), but halal biologics could unlock $125B global market (IMARC).",
        productsServicesNeeded: [
          "Market access consultants",
          "Halal certification agencies"
        ],
        keyPlayers: [
          "Saudi Pharma: Biosimilar distribution ([Link](https://saudipharma.sa))",
          "Halal Development Co.: Standards setting ([Link](https://halal.sa))"
        ],
        trends: "Islamic bioeconomy, value-based pricing.",
        challenges: "Price controls, insurer resistance.",
        riskRating: "Medium-High (4/5) – Demand uncertainty.",
        policyRecommendations: [
          "Procurement quotas for locally made biologics.",
          "Export subsidies for halal-certified products."
        ]
      },
      {
        id: 'export',
        name: 'Export',
        content: 'Export'
      }
    ]
  },

  // Conventional Pharma Sub-Industry
  conventional: {
    type: 'subIndustry',
    parentId: 'pharma',
    stages: [
      {
        id: 'chemical-synthesis',
        name: 'Chemical Synthesis',
        content: 'Organic synthesis, catalysis, process chemistry, scale-up operations'
      },
      {
        id: 'api-manufacturing',
        name: 'API Manufacturing',
        content: 'API production, purification, crystallization, milling, quality testing'
      },
      {
        id: 'formulation',
        name: 'Formulation',
        content: 'Tablet formulation, liquid formulations, controlled release technologies'
      },
      {
        id: 'quality-control',
        name: 'Quality Control',
        content: 'Analytical testing, stability studies, impurity profiling, batch release'
      },
      {
        id: 'packaging-conventional',
        name: 'Packaging',
        content: 'Blister packaging, bottle filling, labeling, secondary packaging, serialization'
      }
    ]
  },

  // Generics Sub-Industry
  generics: {
    type: 'subIndustry',
    parentId: 'pharma',
    stages: [
      {
        id: 'reverse-engineering',
        name: 'Reverse Engineering',
        content: 'Product characterization, formulation analysis, process development'
      },
      {
        id: 'bioequivalence',
        name: 'Bioequivalence Testing',
        content: 'In vitro dissolution, in vivo bioequivalence studies, pharmacokinetic analysis'
      },
      {
        id: 'mass-production',
        name: 'Mass Production',
        content: 'High-volume manufacturing, cost optimization, process efficiency'
      },
      {
        id: 'regulatory-approval',
        name: 'Regulatory Approval',
        content: 'ANDA filings, CTD preparation, regulatory submissions, SFDA approval'
      },
      {
        id: 'market-access',
        name: 'Market Access',
        content: 'Pricing strategies, tender participation, hospital formulary inclusion'
      }
    ]
  },

  // Medical Devices Sub-Industry
  'medical-devices': {
    type: 'subIndustry',
    parentId: 'pharma',
    stages: [
      {
        id: 'device-design',
        name: 'Device Design & Engineering',
        content: 'CAD design, material selection, user interface design, regulatory compliance'
      },
      {
        id: 'prototyping',
        name: 'Prototyping & Testing',
        content: '3D printing, functional prototypes, bench testing, design verification'
      },
      {
        id: 'clinical-validation',
        name: 'Clinical Validation',
        content: 'Clinical investigations, human factors studies, safety evaluation'
      },
      {
        id: 'device-manufacturing',
        name: 'Manufacturing',
        content: 'Clean room production, assembly, sterilization, packaging'
      },
      {
        id: 'distribution-support',
        name: 'Distribution & Support',
        content: 'Logistics, technical support, training, maintenance services'
      }
    ]
  },

  // Aerospace Industry
  aerospace: {
    type: 'industry',
    stages: [
      {
        id: 'design-engineering',
        name: 'Design & Engineering',
        content: 'Aerodynamic design, structural analysis, systems engineering, simulation'
      },
      {
        id: 'components',
        name: 'Components Manufacturing',
        content: 'Precision machining, composite fabrication, electronic components'
      },
      {
        id: 'aircraft-manufacturing',
        name: 'Aircraft Manufacturing',
        content: 'Airframe assembly, systems integration, interior installation'
      },
      {
        id: 'avionics-development',
        name: 'Avionics Development',
        content: 'Flight control systems, navigation, communication, radar systems'
      },
      {
        id: 'testing-certification',
        name: 'Testing & Certification',
        content: 'Ground testing, flight testing, certification to aviation standards'
      },
      {
        id: 'mro',
        name: 'MRO (Maintenance, Repair, Overhaul)',
        content: 'Component overhaul, airframe maintenance, engine servicing'
      },
      {
        id: 'training',
        name: 'Training & Simulation',
        content: 'Flight simulators, pilot training, maintenance personnel training'
      }
    ]
  },

  // Automotive Industry
  automotive: {
    type: 'industry',
    stages: [
      {
        id: 'design',
        name: 'Automotive Design',
        content: 'Vehicle styling, engineering design, CAD modeling, prototyping'
      },
      {
        id: 'component-manufacturing',
        name: 'Component Manufacturing',
        content: 'Engine components, transmission parts, electronic systems, interior parts'
      },
      {
        id: 'vehicle-assembly',
        name: 'Vehicle Assembly',
        content: 'Body assembly, powertrain installation, final assembly, quality control'
      },
      {
        id: 'testing-quality',
        name: 'Testing & Quality Assurance',
        content: 'Crash testing, durability testing, emissions compliance, quality inspection'
      },
      {
        id: 'supply-chain',
        name: 'Supply Chain & Logistics',
        content: 'Just-in-time delivery, inventory management, supplier coordination'
      },
      {
        id: 'sales-marketing',
        name: 'Sales & Marketing',
        content: 'Dealership networks, digital marketing, customer relationship management'
      },
      {
        id: 'after-sales',
        name: 'After-Sales Services',
        content: 'Warranty service, spare parts, vehicle maintenance, customer support'
      }
    ]
  },

  // Electric Vehicles Sub-Industry
  ev: {
    type: 'subIndustry',
    parentId: 'automotive',
    stages: [
      {
        id: 'battery-tech',
        name: 'Battery Technology',
        content: 'Lithium-ion cell production, battery pack assembly, thermal management'
      },
      {
        id: 'ev-motors',
        name: 'Electric Motors',
        content: 'Permanent magnet motors, motor controllers, power electronics'
      },
      {
        id: 'ev-assembly',
        name: 'EV Assembly',
        content: 'Battery installation, electric drivetrain assembly, final vehicle assembly'
      },
      {
        id: 'charging-infra',
        name: 'Charging Infrastructure',
        content: 'Fast charging stations, home charging equipment, grid integration'
      },
      {
        id: 'power-management',
        name: 'Power Management',
        content: 'Battery management systems, energy efficiency optimization, software development'
      }
    ]
  },

  // Spare Parts & Components Sub-Industry
  'spare-parts': {
    type: 'subIndustry',
    parentId: 'automotive',
    stages: [
      {
        id: 'materials',
        name: 'Materials',
        content: 'Metals, polymers, composites, electronics components, raw material sourcing'
      },
      {
        id: 'design-engineering',
        name: 'Design & Engineering',
        content: 'OEM specification matching, reverse engineering, CAD/CAM development'
      },
      {
        id: 'manufacturing',
        name: 'Manufacturing',
        content: 'CNC machining, injection molding, die casting, precision engineering'
      },
      {
        id: 'quality-testing',
        name: 'Quality Testing',
        content: 'Dimensional inspection, material testing, durability testing, performance validation'
      },
      {
        id: 'distribution',
        name: 'Distribution',
        content: 'Warehousing, inventory management, distributor networks, e-commerce'
      }
    ]
  },

  // Tourism & Hospitality Industry
  tourism: {
    type: 'industry',
    stages: [
      {
        id: 'tourism-infrastructure',
        name: 'Tourism Infrastructure',
        content: 'Hotels, resorts, transportation networks, tourist facilities development'
      },
      {
        id: 'destination-development',
        name: 'Destination Development',
        content: 'Cultural sites, entertainment venues, natural attractions enhancement'
      },
      {
        id: 'hospitality-services',
        name: 'Hospitality Services',
        content: 'Accommodation services, food and beverage, concierge, guest experience'
      },
      {
        id: 'tour-operations',
        name: 'Tour Operations',
        content: 'Guided tours, experience packages, adventure tourism, cultural experiences'
      },
      {
        id: 'event-management',
        name: 'Event Management',
        content: 'Conferences, exhibitions, cultural events, festivals, sports events'
      },
      {
        id: 'marketing-promotion',
        name: 'Marketing & Promotion',
        content: 'Digital marketing, international promotion, travel agencies, tourism campaigns'
      }
    ]
  },

  // Religious Tourism Sub-Industry
  religious: {
    type: 'subIndustry',
    parentId: 'tourism',
    stages: [
      {
        id: 'pilgrimage-facilities',
        name: 'Pilgrimage Facilities',
        content: 'Sacred site infrastructure, visitor centers, prayer facilities, accommodation'
      },
      {
        id: 'hajj-services',
        name: 'Hajj Services',
        content: 'Specialized Hajj permits, Mina tent city, Arafat arrangements, transportation'
      },
      {
        id: 'umrah-services',
        name: 'Umrah Services',
        content: 'Umrah packages, visa services, guided religious tours, accommodation'
      },
      {
        id: 'pilgrim-accommodation',
        name: 'Pilgrim Accommodation',
        content: 'Hotels near holy sites, temporary accommodations, serviced apartments'
      },
      {
        id: 'logistics-transport',
        name: 'Logistics & Transport',
        content: 'Mass transportation systems, crowd management, pilgrim movement planning'
      }
    ]
  },

  // Petroleum & Energy Industry
  petroleum: {
    type: 'industry',
    stages: [
      {
        id: 'exploration',
        name: 'Exploration',
        content: 'Seismic surveys, geological analysis, exploration drilling, reservoir assessment'
      },
      {
        id: 'extraction',
        name: 'Extraction',
        content: 'Drilling operations, production wells, offshore platforms, pumping systems'
      },
      {
        id: 'processing',
        name: 'Processing',
        content: 'Separation, gas processing, initial treatment, field processing facilities'
      },
      {
        id: 'refining',
        name: 'Refining',
        content: 'Distillation, cracking, reforming, treatment, blending operations'
      },
      {
        id: 'petrochemicals',
        name: 'Petrochemicals',
        content: 'Olefins, aromatics, polymers, specialty chemicals production'
      },
      {
        id: 'storage',
        name: 'Storage',
        content: 'Tank farms, underground storage, terminal operations, inventory management'
      },
      {
        id: 'distribution',
        name: 'Distribution & Export',
        content: 'Pipeline networks, tanker shipping, export terminals, global trading'
      }
    ]
  },

  // Mining & Minerals Industry
  mining: {
    type: 'industry',
    stages: [
      {
        id: 'exploration',
        name: 'Exploration',
        content: 'Geological surveys, remote sensing, exploration drilling, resource estimation'
      },
      {
        id: 'extraction',
        name: 'Extraction',
        content: 'Open-pit mining, underground mining, quarrying, mineral extraction'
      },
      {
        id: 'processing',
        name: 'Processing',
        content: 'Crushing, grinding, flotation, leaching, concentration, separation'
      },
      {
        id: 'metallurgy',
        name: 'Metallurgy',
        content: 'Smelting, refining, alloying, metal purification, pyrometallurgy'
      },
      {
        id: 'distribution',
        name: 'Distribution',
        content: 'Bulk cargo shipping, rail transport, commodity trading, market distribution'
      },
      {
        id: 'reclamation',
        name: 'Reclamation',
        content: 'Mine closure, land rehabilitation, waste management, environmental restoration'
      }
    ]
  },

  // Food & Beverages Industry
  'food-beverages': {
    type: 'industry',
    stages: [
      {
        id: 'raw-materials',
        name: 'Raw Materials',
        content: 'Agricultural inputs, commodity sourcing, ingredient supply, quality control'
      },
      {
        id: 'processing',
        name: 'Processing',
        content: 'Primary processing, cooking, fermentation, preservation, formulation'
      },
      {
        id: 'packaging',
        name: 'Packaging',
        content: 'Primary packaging, secondary packaging, labeling, preservation technology'
      },
      {
        id: 'quality-safety',
        name: 'Quality & Safety',
        content: 'Microbiological testing, chemical analysis, HACCP, food safety certification'
      },
      {
        id: 'distribution',
        name: 'Distribution',
        content: 'Cold chain logistics, warehousing, transport, distribution centers'
      },
      {
        id: 'retail',
        name: 'Retail',
        content: 'Supermarkets, specialty stores, foodservice, e-commerce platforms'
      }
    ]
  },

  // Renewable Energy Industry
  'renewable-energy': {
    type: 'industry',
    stages: [
      {
        id: 'r-and-d',
        name: 'Research & Development',
        content: 'Technology innovation, efficiency improvement, materials research, prototyping'
      },
      {
        id: 'manufacturing',
        name: 'Manufacturing',
        content: 'Component production, assembly, quality control, equipment manufacturing'
      },
      {
        id: 'installation',
        name: 'Installation',
        content: 'Site preparation, equipment installation, commissioning, grid connection'
      },
      {
        id: 'grid-integration',
        name: 'Grid Integration',
        content: 'Grid connection infrastructure, substations, transmission, distribution'
      },
      {
        id: 'operations',
        name: 'Operations & Maintenance',
        content: 'Performance monitoring, preventive maintenance, repairs, system optimization'
      },
      {
        id: 'energy-storage',
        name: 'Energy Storage',
        content: 'Battery systems, thermal storage, hydrogen storage, pumped hydro'
      }
    ]
  },

  // Solar Energy Sub-Industry
  solar: {
    type: 'subIndustry',
    parentId: 'renewable-energy',
    stages: [
      {
        id: 'pv-manufacturing',
        name: 'PV Manufacturing',
        content: 'Silicon processing, cell production, module assembly, quality testing'
      },
      {
        id: 'csp',
        name: 'Concentrated Solar Power',
        content: 'Mirror arrays, thermal receivers, heat transfer systems, power blocks'
      },
      {
        id: 'solar-installation',
        name: 'Installation',
        content: 'Site assessment, mounting systems, panel installation, electrical connections'
      },
      {
        id: 'solar-operations',
        name: 'Operations & Maintenance',
        content: 'Panel cleaning, performance monitoring, inverter maintenance, troubleshooting'
      },
      {
        id: 'solar-storage',
        name: 'Energy Storage Integration',
        content: 'Battery installation, energy management systems, grid interaction, load management'
      }
    ]
  },

  // Healthcare Services Industry
  healthcare: {
    type: 'industry',
    stages: [
      {
        id: 'primary-care',
        name: 'Primary Care',
        content: 'General practice, preventive care, health screening, community health'
      },
      {
        id: 'specialty-care',
        name: 'Specialty Care',
        content: 'Specialized medical services, surgery, oncology, cardiology, orthopedics'
      },
      {
        id: 'diagnostics',
        name: 'Diagnostics',
        content: 'Laboratory testing, medical imaging, pathology, point-of-care diagnostics'
      },
      {
        id: 'hospital-services',
        name: 'Hospital Services',
        content: 'Inpatient care, emergency services, intensive care, surgical facilities'
      },
      {
        id: 'rehabilitation',
        name: 'Rehabilitation',
        content: 'Physical therapy, occupational therapy, speech therapy, rehabilitation centers'
      },
      {
        id: 'digital-health',
        name: 'Digital Health',
        content: 'Telemedicine, health apps, electronic health records, remote monitoring'
      }
    ]
  },

  // Logistics & Supply Chain Industry
  logistics: {
    type: 'industry',
    stages: [
      {
        id: 'transportation',
        name: 'Transportation',
        content: 'Trucking, air freight, sea shipping, rail transport, multimodal logistics'
      },
      {
        id: 'warehousing',
        name: 'Warehousing',
        content: 'Storage facilities, inventory management, order fulfillment, cross-docking'
      },
      {
        id: 'customs',
        name: 'Customs & Compliance',
        content: 'Customs clearance, documentation, regulatory compliance, trade facilitation'
      },
      {
        id: 'distribution',
        name: 'Distribution',
        content: 'Last-mile delivery, distribution centers, route optimization, fulfillment'
      },
      {
        id: 'supply-chain-tech',
        name: 'Supply Chain Technology',
        content: 'Track and trace systems, ERP integration, blockchain, IoT applications'
      },
      {
        id: 'logistics-consulting',
        name: 'Logistics Consulting',
        content: 'Supply chain optimization, network design, logistics strategy, process improvement'
      }
    ]
  }
};